import { z } from 'zod';
import { IdSchema } from './common.js';
import type { CallScript } from './scripts.js';

/**
 * Dialler campaigns (docs/36 D1b, 0127): the rules every part of the platform has to agree on --
 * when a person may lawfully be called, and what happens to a lead after each call.
 */

// =================================================================================== legal hours

/**
 * South Africa's public holidays for a year (Public Holidays Act 36 of 1994), with the Act's rule
 * that a holiday on a Sunday moves to the Monday. Good Friday and Family Day follow Easter.
 * Returned as YYYY-MM-DD.
 */
export function saPublicHolidays(year: number): string[] {
  const d = (m: number, day: number) => new Date(Date.UTC(year, m - 1, day));
  // Easter Sunday, Anonymous Gregorian algorithm.
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const dd = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - dd - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  const easter = d(month, day);
  const plus = (base: Date, n: number) => new Date(base.getTime() + n * 86_400_000);

  const fixed = [d(1, 1), d(3, 21), d(4, 27), d(5, 1), d(6, 16), d(8, 9), d(9, 24), d(12, 16), d(12, 25), d(12, 26)];
  const out = new Set<string>();
  for (const x of fixed) {
    out.add(x.toISOString().slice(0, 10));
    if (x.getUTCDay() === 0) out.add(plus(x, 1).toISOString().slice(0, 10));
  }
  out.add(plus(easter, -2).toISOString().slice(0, 10)); // Good Friday
  out.add(plus(easter, 1).toISOString().slice(0, 10)); // Family Day
  return [...out].sort();
}

/**
 * The hard floor on when a campaign may call (docs/36 §9, CPA regulation 4 on direct marketing):
 * Monday to Friday 08:00-20:00, Saturday 09:00-13:00, never on a Sunday or a public holiday. No
 * campaign setting can widen it. In the account's time zone.
 */
export const LEGAL_HOURS: Record<number, [string, string] | null> = {
  1: ['08:00', '20:00'], 2: ['08:00', '20:00'], 3: ['08:00', '20:00'], 4: ['08:00', '20:00'], 5: ['08:00', '20:00'],
  6: ['09:00', '13:00'], 7: null,
};

export interface LocalNow { date: string; isoDay: number; time: string }

/** The account's date, ISO weekday (1 Monday) and HH:MM, for an instant. */
export function localNow(at: Date, timeZone: string): LocalNow {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    weekday: 'short', hourCycle: 'h23',
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const isoDay = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(get('weekday')) + 1;
  return { date: `${get('year')}-${get('month')}-${get('day')}`, isoDay, time: `${get('hour')}:${get('minute')}` };
}

export interface CallWindow { window_start: string; window_end: string; window_days: number[] }

/**
 * May this campaign call now? Why not, in words, when it may not. The campaign's own window is
 * applied inside the legal one; an account's own holidays (closed days) are passed in too.
 */
export function mayCallNow(
  at: Date, timeZone: string, w: CallWindow, extraClosedDates: readonly string[] = [],
): { ok: true } | { ok: false; why: string } {
  const now = localNow(at, timeZone);
  const legal = LEGAL_HOURS[now.isoDay];
  if (!legal) return { ok: false, why: 'Sundays are closed to campaigns by law.' };
  if (saPublicHolidays(Number(now.date.slice(0, 4))).includes(now.date)) {
    return { ok: false, why: 'Public holidays are closed to campaigns by law.' };
  }
  if (extraClosedDates.includes(now.date)) return { ok: false, why: 'The account is closed today.' };
  if (now.time < legal[0] || now.time >= legal[1]) {
    return { ok: false, why: `Campaigns may call from ${legal[0]} to ${legal[1]} today.` };
  }
  if (!w.window_days.includes(now.isoDay)) return { ok: false, why: 'The campaign does not call today.' };
  const start = w.window_start.slice(0, 5), end = w.window_end.slice(0, 5);
  if (now.time < start || now.time >= end) return { ok: false, why: `The campaign calls from ${start} to ${end}.` };
  return { ok: true };
}

// =================================================================================== after a call

export const CALL_OUTCOMES = [
  'answered', 'no_answer', 'busy', 'failed', 'machine', 'abandoned', 'cancelled',
  'optout', 'transferred', 'message', 'no_key',
] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];

/** What a disposition does to the contact (0128): reached, try again, or this number is wrong. */
export const DISPOSITION_EFFECTS = ['final', 'retry', 'next_number'] as const;
export type DispositionEffect = (typeof DISPOSITION_EFFECTS)[number];

/**
 * How long a contact waits before its next try, by the outcome or disposition that ended the last
 * one. `max` is kept for campaigns saved before 0128 and no longer read: how many tries a contact
 * gets is its LIST's setting (owner, 26 September 2026).
 */
export type RecycleRules = Record<string, { after_min: number; max?: number }>;

export const DEFAULT_RECYCLE: RecycleRules = {
  busy: { after_min: 20 },
  no_answer: { after_min: 180 },
  machine: { after_min: 360 },
  failed: { after_min: 5 },
};
const DEFAULT_WAIT_MIN = 180;

/** Answered and dealt with: a person was spoken to, or took the message. */
const CONNECTED: ReadonlySet<CallOutcome> = new Set(['answered', 'transferred', 'message', 'no_key']);

export interface AfterCall {
  /** dialable: waiting for its next call (now, when continueNow). done: finished. suppressed: do not call. */
  state: 'dialable' | 'done' | 'suppressed';
  /** Call again at once, on numberIndex, within this try: the next number, or a redial. */
  continueNow: boolean;
  nextEligibleAt: Date;
  numberIndex: number;
  triesUsed: number;
  reached: boolean;
  reason: string;
}

/**
 * What becomes of a contact after one call (docs/36 §5.3; owner, 26 September 2026).
 *
 * A TRY is one pass through the contact's numbers in order. A call that does not reach the contact
 * moves STRAIGHT ON to its next number; when every number has been tried, the try is used up and the
 * contact waits (by the campaign's wait for that outcome) for its next try, up to the LIST's number
 * of tries. A contact answered with a final disposition is REACHED and never called again.
 *
 * The agent can decide otherwise on a call that did not reach anyone: `redial` calls the same
 * number again now, and `callback` books it for a time they choose. Neither uses up a try.
 */
export function afterCall(input: {
  outcome: CallOutcome;
  disposition: string | null;
  /** The disposition's effect; null when there is no disposition. */
  effect: DispositionEffect | null;
  numbers: number;
  numberIndex: number;
  triesUsed: number;
  /** The list's tries (0128). */
  tries: number;
  /** Calls this contact has had in this campaign, this one included. */
  attempts: number;
  maxAttempts: number;
  waits: RecycleRules;
  choice?: 'redial' | 'callback' | null;
  callbackAt?: Date | null;
  now: Date;
}): AfterCall {
  const at = input.now;
  const same = { numberIndex: input.numberIndex, triesUsed: input.triesUsed, reached: false };
  if (input.outcome === 'optout') return { state: 'suppressed', continueNow: false, nextEligibleAt: at, ...same, reason: 'opted out' };
  // Skipped before dialling: nothing happened to the contact.
  if (input.outcome === 'cancelled') return { state: 'dialable', continueNow: false, nextEligibleAt: at, ...same, reason: 'skipped' };

  const connected = CONNECTED.has(input.outcome);
  const effect: DispositionEffect = input.effect ?? (connected ? 'final' : 'retry');
  if (connected && effect === 'final') {
    return { state: 'done', continueNow: false, nextEligibleAt: at, ...same, reached: true, reason: 'reached' };
  }

  // The agent's own choice, on a call that reached nobody.
  if (input.choice === 'callback' && input.callbackAt) {
    return { state: 'dialable', continueNow: false, nextEligibleAt: input.callbackAt, ...same, reason: 'callback booked' };
  }
  if (input.attempts >= input.maxAttempts) return { state: 'done', continueNow: false, nextEligibleAt: at, ...same, reason: 'call limit' };
  if (input.choice === 'redial') return { state: 'dialable', continueNow: true, nextEligibleAt: at, ...same, reason: 'redial' };

  const last = input.numberIndex + 1 >= input.numbers;
  if (!last) {
    return { state: 'dialable', continueNow: true, nextEligibleAt: at, numberIndex: input.numberIndex + 1,
      triesUsed: input.triesUsed, reached: false, reason: effect === 'next_number' ? 'wrong number, next number' : 'next number' };
  }
  if (effect === 'next_number') {
    return { state: 'done', continueNow: false, nextEligibleAt: at, ...same, reason: 'no working number' };
  }
  // Every number tried: this try is used up.
  const triesUsed = input.triesUsed + 1;
  if (triesUsed >= input.tries) {
    return { state: 'done', continueNow: false, nextEligibleAt: at, numberIndex: 0, triesUsed, reached: false, reason: `not reached in ${input.tries} tries` };
  }
  const key = input.disposition ?? input.outcome;
  const wait = input.waits[key]?.after_min ?? input.waits[input.outcome]?.after_min ?? DEFAULT_WAIT_MIN;
  return { state: 'dialable', continueNow: false, nextEligibleAt: new Date(at.getTime() + wait * 60_000),
    numberIndex: 0, triesUsed, reached: false, reason: key };
}

// =================================================================================== the API

const Time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'HH:MM');
const Destination = z.object({ type: z.enum(['queue', 'ring_group', 'extension', 'ivr']), id: IdSchema });

/** A disposition that belongs to one campaign (0136): the account's are managed under Agent codes. */
export const CampaignDispositionSchema = z.object({
  code: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
  label: z.string().trim().min(1).max(60),
  kind: z.enum(['positive', 'neutral', 'negative']).default('neutral'),
  effect: z.enum(['final', 'retry', 'next_number']).default('final'),
  requires_note: z.boolean().default(false),
}).strict();
export type CampaignDisposition = z.infer<typeof CampaignDispositionSchema>;

/** What an agent must give before a campaign call is done, sent with the lead (0136). */
export interface WrapUpRules {
  dispositions: Array<CampaignDisposition & { own: boolean }>;
  note_required: boolean;
  reference_label: string | null;
  reference_required: boolean;
  tags: string[];
  tags_required: boolean;
}

/** The modes where agents work the leads from the workspace, as opposed to auto's message. */
export const agentMode = (mode: string): boolean => mode === 'preview' || mode === 'progressive';

export const CAMPAIGN_MODE_LABEL: Record<string, string> = {
  preview: 'Preview', progressive: 'Progressive', auto: 'Auto', power: 'Power', predictive: 'Predictive',
};

export const CampaignUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  mode: z.enum(['preview', 'progressive', 'auto']),
  window_start: Time.default('08:00'),
  window_end: Time.default('20:00'),
  window_days: z.array(z.number().int().min(1).max(7)).min(1).max(7).default([1, 2, 3, 4, 5, 6]),
  lead_order: z.enum(['oldest', 'newest', 'random']).default('oldest'),
  caller_id: z.string().regex(/^[1-9][0-9]{7,14}$/).nullable().optional(),
  /** A number pool (0137): the caller ID rotates through it. Takes the place of caller_id. */
  cli_pool_id: IdSchema.nullable().optional(),
  max_attempts: z.number().int().min(1).max(50).default(8),
  recycle: z.record(z.string().regex(/^[a-z][a-z0-9_]{1,30}$/), z.object({
    after_min: z.number().int().min(1).max(60 * 24 * 30),
    max: z.number().int().min(1).max(20).optional(),
  })).default(DEFAULT_RECYCLE),
  sticky_agent_days: z.number().int().min(1).max(365).nullable().optional(),
  list_ids: z.array(IdSchema).max(50).default([]),
  agent_extension_ids: z.array(IdSchema).max(500).default([]),
  /** The call script agents see (preview campaigns, 0129). */
  script_id: IdSchema.nullable().optional(),
  /** Preview: dial the lead on the screen after this many seconds unless the agent holds it (0139). Null: off. */
  preview_autodial_sec: z.number().int().min(3).max(120).nullable().optional(),
  // auto
  billing_extension_id: IdSchema.nullable().optional(),
  message_media_id: IdSchema.nullable().optional(),
  transfer_destination: Destination.nullable().optional(),
  transfer_key: z.string().regex(/^[0-9*#]$/).default('1'),
  optout_key: z.string().regex(/^[0-9*#]$/).default('9'),
  max_lines: z.number().int().min(1).max(200).default(5),
  amd_mode: z.enum(['off', 'own', 'own_then_ai', 'ai']).default('off'),
  on_machine: z.enum(['hangup', 'message']).default('hangup'),
  /** Played to a phone's own call screen, saying who is calling and why (0135). */
  screen_reply_media_id: IdSchema.nullable().optional(),
  /** Each agent's priority for this campaign, 1 first to 9 last (0136); 5 when not given. */
  agent_priorities: z.record(IdSchema, z.number().int().min(1).max(9)).default({}),
  /** Offer the account's own dispositions beside the campaign's (0136). */
  offer_account_dispositions: z.boolean().default(true),
  /** This campaign's own dispositions (0136). */
  dispositions: z.array(CampaignDispositionSchema).max(50).default([]),
  /** What an answered call needs before it is done (0136). */
  note_required: z.boolean().default(false),
  reference_label: z.string().trim().min(1).max(40).nullable().optional(),
  reference_required: z.boolean().default(false),
  tags: z.array(z.string().trim().min(1).max(30)).max(30).default([]),
  tags_required: z.boolean().default(false),
}).strict().superRefine((c, issue) => {
  if (c.window_end <= c.window_start) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['window_end'], message: 'Ends before it starts.' });
  if (c.transfer_key === c.optout_key) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['optout_key'], message: 'Not the same key as transfer.' });
  if (c.reference_required && !c.reference_label) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['reference_label'], message: 'Name the reference the agent must enter.' });
  if (c.tags_required && c.tags.length === 0) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['tags'], message: 'Add the tags agents choose from.' });
  const codes = c.dispositions.map((d) => d.code);
  if (new Set(codes).size !== codes.length) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['dispositions'], message: 'Each disposition needs its own code.' });
  if (!c.offer_account_dispositions && c.dispositions.length === 0 && agentMode(c.mode)) {
    issue.addIssue({ code: z.ZodIssueCode.custom, path: ['dispositions'], message: 'Add the campaign’s dispositions, or offer the account’s.' });
  }
});
export type CampaignUpsert = z.infer<typeof CampaignUpsertSchema>;

export interface Campaign {
  id: string; name: string; description: string | null;
  mode: 'preview' | 'auto' | 'progressive' | 'power' | 'predictive';
  status: 'draft' | 'running' | 'paused' | 'finished';
  window_start: string; window_end: string; window_days: number[];
  lead_order: 'oldest' | 'newest' | 'random';
  caller_id: string | null; cli_pool_id: string | null; max_attempts: number; recycle: RecycleRules; sticky_agent_days: number | null;
  billing_extension_id: string | null; message_media_id: string | null;
  transfer_destination: { type: string; id: string } | null;
  transfer_key: string; optout_key: string; max_lines: number;
  amd_mode: 'off' | 'own' | 'own_then_ai' | 'ai'; on_machine: 'hangup' | 'message';
  screen_reply_media_id: string | null;
  agent_priorities: Record<string, number>;
  offer_account_dispositions: boolean;
  dispositions: CampaignDisposition[];
  note_required: boolean; reference_label: string | null; reference_required: boolean;
  tags: string[]; tags_required: boolean;
  list_ids: string[]; agent_extension_ids: string[]; script_id: string | null;
  preview_autodial_sec: number | null;
  counts: { dialable: number; due_now: number; leased: number; done: number; suppressed: number; total: number };
  created_at: string; started_at: string | null;
}

/** What an agent sees when a preview campaign gives them a lead. */
export interface PreviewLead {
  campaign_record_id: string;
  attempt_id: string;
  campaign: { id: string; name: string };
  /** The number to call now: `numbers[number_index]`. */
  phone: string;
  /** All the contact's numbers in dialling order, and which one this call is on. */
  numbers: string[];
  number_index: number;
  /** The try this call belongs to (1-based) and how many the list allows. */
  try_number: number;
  tries: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  fields: Array<{ key: string; label: string; value: string | number | boolean | null }>;
  attempts: number;
  why: 'callback' | 'retry' | 'fresh' | 'yours';
  callback_note: string | null;
  history: Array<{ at: string; phone: string; outcome: string | null; disposition: string | null; note: string | null; agent: string | null }>;
  /** The campaign's script, at the version this call records (0129); null without one. */
  script: CallScript | null;
  /** Everything the script's {{variables}} can name, filled in for this contact and this call. */
  vars: Record<string, string | null>;
  /** What this call needs before it is done (0136). */
  wrap_up: WrapUpRules;
  /** The number this call shows (+E.164, 0137): the campaign's pool or its own; null shows the seat's. */
  caller_id: string | null;
}

export const PreviewResultSchema = z.object({
  outcome: z.enum(CALL_OUTCOMES),
  /** The agent's choice on a call that reached nobody: call again now, or at a time they book. */
  then: z.enum(['redial', 'callback']).nullable().optional(),
  disposition: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  /** The campaign's reference and tags, when it asks for them (0136). */
  reference: z.string().trim().max(100).nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(30)).max(10).optional(),
  callback_at: z.string().datetime({ offset: true }).nullable().optional(),
  callback_mine: z.boolean().default(true),
  do_not_call: z.boolean().default(false),
  /** The script's fields, as the agent filled them in: written back to the contact. */
  captures: z.record(z.string().regex(/^[a-z][a-z0-9_]{0,39}$/), z.union([z.string().max(500), z.number(), z.boolean(), z.null()])).optional(),
}).strict();
